// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// Squarebit Eye product pages / widget (store front end). The Eye software
// itself is licensed separately under the Squarebit Eye End User License
// Agreement: https://store.squarebitstudios.com/squarebit-eye/eula
// ==========================================================================

import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Note, Tip, VarCard } from '../../touchpose/ui';
import { H2, H3, Body, DocHero, DocFooter } from './shared';

/**
 * Full artist-dial reference. Groups mirror the live widget's tabs one-to-one
 * (Iris & pupil / Procedural iris / Limbus / Cornea & refraction / Placed
 * highlight / Light & view), so the widget and the docs share one mental
 * model. Each entry: range, default, one-line artist description.
 *
 * i18n: the dial *names* and the group headings reuse the widget's own
 * squarebitEye.* keys, so a dial is called the same thing in the docs as on
 * the slider the artist is actually dragging, in every language. Only the
 * descriptions are new copy (eyeDocs.parameters.*). The range and default
 * columns are never translated — they are literal values.
 */
const LINK = 'text-blue-link no-underline font-medium';

/** One dial: translated name + description, verbatim range and default. */
const Dial: React.FC<{
  nameKey: string;
  range: string;
  def: string;
  children: React.ReactNode;
}> = ({ nameKey, range, def, children }) => {
  const { t } = useTranslation();
  return (
    <VarCard name={t(nameKey)} type={range} example={def}>
      {children}
    </VarCard>
  );
};

const ParametersDoc: React.FC = () => {
  const { t } = useTranslation();

  const noiseTypes = [
    t('squarebitEye.noiseLattice'),
    t('squarebitEye.noiseBillow'),
    t('squarebitEye.noiseRidged'),
    t('squarebitEye.noiseScallop')
  ].join(' | ');
  const fiberStyles = [
    t('squarebitEye.fiberWispy'),
    t('squarebitEye.noiseBillow'),
    t('squarebitEye.noiseRidged')
  ].join(' | ');
  const colorType = t('eyeDocs.parameters.typeColor');

  return (
    <article className="font-body text-content-primary max-w-[760px]">
      <DocHero
        title={t('eyeDocs.parameters.title')}
        intro={t('eyeDocs.parameters.intro')}
      />
      <Note label={t('eyeDocs.parameters.noteSameLabel')}>
        {t('eyeDocs.parameters.noteSame')}
      </Note>

      <H2 id="iris-pupil">{t('squarebitEye.sectionIris')}</H2>

      <H3>{t('squarebitEye.groupIris')}</H3>
      {/* Documented with the iris because that is what it sizes. It was
          under Limbus, which named the edge rather than the thing - and the
          widget now groups it here too, so the two agree. */}
      <Dial nameKey="squarebitEye.irisRadiusLabel" range="0.25–0.7" def="0.4875">
        {t('eyeDocs.parameters.limbusRadius')}
      </Dial>
      <Dial nameKey="squarebitEye.irisWidthLabel" range="0.25–2" def="1.0">
        {t('eyeDocs.parameters.irisWidth')}
      </Dial>
      <Dial nameKey="squarebitEye.irisHeightLabel" range="0.25–2" def="1.0">
        {t('eyeDocs.parameters.irisHeight')}
      </Dial>
      <Dial nameKey="squarebitEye.irisDepthAoLabel" range="0–1" def="0">
        {t('eyeDocs.parameters.irisDepthAo')}
      </Dial>

      <H3>{t('squarebitEye.groupPupilShape')}</H3>
      <Dial nameKey="squarebitEye.pupilLabel" range="0.05–0.42" def="0.1667">
        {t('eyeDocs.parameters.pupilSize')}
      </Dial>
      <Dial nameKey="squarebitEye.pupilWidthLabel" range="0.25–2" def="1.0">
        {t('eyeDocs.parameters.pupilWidth')}
      </Dial>
      <Dial nameKey="squarebitEye.pupilHeightLabel" range="0.25–2" def="1.0">
        {t('eyeDocs.parameters.pupilHeight')}
      </Dial>
      <Dial nameKey="squarebitEye.pupilOffsetXLabel" range="−1–1" def="0">
        {t('eyeDocs.parameters.pupilOffsetX')}
      </Dial>
      <Dial nameKey="squarebitEye.pupilOffsetYLabel" range="−1–1" def="0">
        {t('eyeDocs.parameters.pupilOffsetY')}
      </Dial>
      <Dial
        nameKey="eyeDocs.parameters.pupilBlendName"
        range="0–0.5"
        def="0.08"
      >
        {t('eyeDocs.parameters.pupilBlend')}
      </Dial>
      <Dial
        nameKey="eyeDocs.parameters.pupilBlendFalloffName"
        range="0.5–4"
        def="1.5"
      >
        {t('eyeDocs.parameters.pupilBlendFalloff')}
      </Dial>
      <Dial nameKey="squarebitEye.pupilSquarenessLabel" range="0–1" def="0">
        {t('eyeDocs.parameters.pupilSquareness')}
      </Dial>

      <H3>{t('squarebitEye.groupEdgeNoise')}</H3>
      <Dial
        nameKey="squarebitEye.pupilNoiseTypeLabel"
        range={noiseTypes}
        def={t('squarebitEye.noiseLattice')}
      >
        <Trans
          i18nKey="eyeDocs.parameters.pupilNoiseType"
          values={{
            lattice: t('squarebitEye.noiseLattice'),
            billow: t('squarebitEye.noiseBillow'),
            ridged: t('squarebitEye.noiseRidged'),
            scallop: t('squarebitEye.noiseScallop')
          }}
          components={{ b: <strong /> }}
        />
      </Dial>
      <Dial nameKey="squarebitEye.pupilNoiseLabel" range="0–0.35" def="0">
        {t('eyeDocs.parameters.pupilNoise')}
      </Dial>
      <Dial nameKey="squarebitEye.pupilNoiseFreqLabel" range="2–32" def="12">
        {t('eyeDocs.parameters.noiseFreq')}
      </Dial>
      <Dial nameKey="squarebitEye.pupilNoiseSeedLabel" range="0–99" def="0">
        {t('eyeDocs.parameters.noiseSeed')}
      </Dial>

      <H3>{t('squarebitEye.groupBleed')}</H3>
      <Dial nameKey="squarebitEye.pupilBleedLabel" range="0–1" def="0">
        {t('eyeDocs.parameters.pupilBleed')}
      </Dial>
      <Dial nameKey="squarebitEye.pupilBleedWidthLabel" range="0.02–1" def="0.3">
        {t('eyeDocs.parameters.bleedWidth')}
      </Dial>
      <Dial nameKey="squarebitEye.pupilBleedExpLabel" range="1–3" def="2">
        {t('eyeDocs.parameters.bleedFalloff')}
      </Dial>
      <Dial nameKey="squarebitEye.pupilBleedLightLabel" range="0–1" def="0">
        {t('eyeDocs.parameters.bleedLightResponse')}
      </Dial>
      <Dial nameKey="squarebitEye.pupilBleedSatLabel" range="0–1" def="0.4">
        {t('eyeDocs.parameters.bleedSaturation')}
      </Dial>

      <H2 id="procedural-iris">{t('eyeDocs.layout.proceduralIris')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.parameters.proceduralIntro"
          components={{
            baker: (
              <Link to="/squarebit-eye/docs/textures#baking" className={LINK} />
            )
          }}
        />
      </Body>

      <H3>{t('eyeDocs.parameters.h3Colors')}</H3>
      <Dial
        nameKey="squarebitEye.innerColorLabel"
        range={colorType}
        def="#78471A"
      >
        {t('eyeDocs.parameters.irisInner')}
      </Dial>
      <Dial
        nameKey="squarebitEye.outerColorLabel"
        range={colorType}
        def="#759E78"
      >
        {t('eyeDocs.parameters.irisOuter')}
      </Dial>
      <Dial
        nameKey="squarebitEye.spotColorLabel"
        range={colorType}
        def="#47230C"
      >
        {t('eyeDocs.parameters.spotColor')}
      </Dial>

      <H3>{t('squarebitEye.groupFibers')}</H3>
      <Dial
        nameKey="squarebitEye.texFiberStyleLabel"
        range={fiberStyles}
        def={t('squarebitEye.noiseBillow')}
      >
        <Trans
          i18nKey="eyeDocs.parameters.fiberStyle"
          values={{
            wispy: t('squarebitEye.fiberWispy'),
            billow: t('squarebitEye.noiseBillow'),
            ridged: t('squarebitEye.noiseRidged')
          }}
          components={{ b: <strong /> }}
        />
      </Dial>
      <Dial nameKey="squarebitEye.texDetailLabel" range="0.4–2.2" def="1.0">
        {t('eyeDocs.parameters.fiberDetail')}
      </Dial>
      <Dial nameKey="squarebitEye.texFiberLabel" range="0.2–1.6" def="0.7">
        {t('eyeDocs.parameters.fiberStrength')}
      </Dial>
      <Dial nameKey="squarebitEye.texFiberWavinessLabel" range="0–1" def="0.3">
        {t('eyeDocs.parameters.fiberWaviness')}
      </Dial>
      <Dial nameKey="squarebitEye.texFiberClumpingLabel" range="0–1" def="0.4">
        {t('eyeDocs.parameters.fiberClumping')}
      </Dial>
      <Dial nameKey="squarebitEye.texFiberGapsLabel" range="0–1" def="0">
        {t('eyeDocs.parameters.fiberGaps')}
      </Dial>
      <Dial nameKey="squarebitEye.texFiberCryptsLabel" range="0–1" def="0.45">
        {t('eyeDocs.parameters.crypts')}
      </Dial>

      <H3>{t('squarebitEye.groupHalo')}</H3>
      <Dial nameKey="squarebitEye.texHaloSizeLabel" range="0.3–2.5" def="1.0">
        {t('eyeDocs.parameters.haloSize')}
      </Dial>
      <Dial nameKey="squarebitEye.texHaloStrengthLabel" range="0–1.5" def="1.0">
        {t('eyeDocs.parameters.haloStrength')}
      </Dial>

      <H3>{t('squarebitEye.groupSpots')}</H3>
      <Dial nameKey="squarebitEye.texSpotDensityLabel" range="0–1" def="0">
        {t('eyeDocs.parameters.spotDensity')}
      </Dial>
      <Dial nameKey="squarebitEye.texSpotScaleLabel" range="0.2–3" def="1.0">
        {t('eyeDocs.parameters.spotSize')}
      </Dial>
      <Dial nameKey="squarebitEye.texSpotSpreadLabel" range="0.2–3" def="1.0">
        {t('eyeDocs.parameters.spotSpread')}
      </Dial>
      <Dial
        nameKey="squarebitEye.texSpotIrregularityLabel"
        range="0–1"
        def="0"
      >
        {t('eyeDocs.parameters.spotIrregularity')}
      </Dial>
      <Dial nameKey="squarebitEye.texSpotOpacityLabel" range="0–1" def="0.9">
        {t('eyeDocs.parameters.spotOpacity')}
      </Dial>
      <Dial nameKey="squarebitEye.texSpotVaryLabel" range="0–1" def="0">
        {t('eyeDocs.parameters.spotVariation')}
      </Dial>
      <Dial nameKey="squarebitEye.texSpotSeedLabel" range="0–99" def="0">
        {t('eyeDocs.parameters.spotSeed')}
      </Dial>

      <H3>{t('squarebitEye.groupHetero')}</H3>
      <Dial nameKey="squarebitEye.heteroAmountLabel" range="0–1" def="0">
        {t('eyeDocs.parameters.heteroCoverage')}
      </Dial>
      <Dial nameKey="squarebitEye.heteroAngleLabel" range="0–360°" def="0">
        {t('eyeDocs.parameters.heteroAngle')}
      </Dial>
      <Dial
        nameKey="squarebitEye.heteroSoftnessLabel"
        range="0.02–0.5"
        def="0.15"
      >
        {t('eyeDocs.parameters.heteroSoftness')}
      </Dial>
      <Dial
        nameKey="squarebitEye.heteroColorLabel"
        range={colorType}
        def="#5A4788"
      >
        {t('eyeDocs.parameters.heteroColor')}
      </Dial>

      <H2 id="limbus">{t('squarebitEye.sectionLimbus')}</H2>
      <Dial nameKey="squarebitEye.limbusWidthLabel" range="0–0.15" def="0.04">
        {t('eyeDocs.parameters.limbusWidth')}
      </Dial>
      <Dial nameKey="squarebitEye.limbusDarkeningLabel" range="0–1" def="0.6">
        {t('eyeDocs.parameters.limbusDarkening')}
      </Dial>
      <Dial nameKey="squarebitEye.limbusNoiseLabel" range="0–1" def="0">
        {t('eyeDocs.parameters.limbusBreakup')}
      </Dial>
      <Dial nameKey="squarebitEye.limbusNoiseFreqLabel" range="2–32" def="8">
        {t('eyeDocs.parameters.breakupFreq')}
      </Dial>
      <Dial nameKey="squarebitEye.limbusNoiseSeedLabel" range="0–99" def="0">
        {t('eyeDocs.parameters.breakupSeed')}
      </Dial>
      <Dial nameKey="squarebitEye.limbusLightRespLabel" range="0–1" def="0.6">
        {t('eyeDocs.parameters.limbusLightResponse')}
      </Dial>
      <Tip label={t('eyeDocs.parameters.tipLimbusTexLabel')}>
        <Trans
          i18nKey="eyeDocs.parameters.tipLimbusTex"
          values={{ page: t('eyeDocs.layout.textures') }}
          components={{
            tex: (
              <Link
                to="/squarebit-eye/docs/textures#limbus-map"
                className={LINK}
              />
            )
          }}
        />
      </Tip>

      <H2 id="cornea">{t('squarebitEye.sectionCornea')}</H2>
      <Dial nameKey="squarebitEye.bulgeLabel" range="0–1.5" def="1.0">
        {t('eyeDocs.parameters.corneaBulge')}
      </Dial>
      <Dial nameKey="squarebitEye.iorLabel" range="1.0–1.6" def="1.376">
        {t('eyeDocs.parameters.corneaIor')}
      </Dial>
      <Dial nameKey="squarebitEye.causticLabel" range="0–4" def="1.0">
        <Trans i18nKey="eyeDocs.parameters.caustic" components={{ i: <em /> }} />
      </Dial>
      <Dial nameKey="squarebitEye.lodLabel" range="0–1" def="0">
        {t('eyeDocs.parameters.lodBlend')}
      </Dial>
      <Dial nameKey="squarebitEye.tearFilmLabel" range="0–1" def="0">
        {t('eyeDocs.parameters.tearFilm')}
      </Dial>

      <H3>{t('eyeDocs.parameters.h3Surface')}</H3>
      <Body>{t('eyeDocs.parameters.surfaceIntro')}</Body>
      <Dial nameKey="eyeDocs.parameters.nameCoatWeight" range="0–1" def="1.0">
        {t('eyeDocs.parameters.coatWeight')}
      </Dial>
      <Dial nameKey="eyeDocs.parameters.nameCoatRoughness" range="0–1" def="0.02">
        {t('eyeDocs.parameters.coatRoughness')}
      </Dial>
      <Dial nameKey="eyeDocs.parameters.nameCoatIor" range="1.0–2.0" def="1.376">
        {t('eyeDocs.parameters.coatIor')}
      </Dial>
      <Dial nameKey="eyeDocs.parameters.nameIrisRoughness" range="0–1" def="0.6">
        {t('eyeDocs.parameters.irisRoughness')}
      </Dial>
      <Dial nameKey="eyeDocs.parameters.nameScleraRoughness" range="0–1" def="0.30">
        {t('eyeDocs.parameters.scleraRoughness')}
      </Dial>
      <Dial
        nameKey="eyeDocs.parameters.nameScleraFilmRoughness"
        range="0–1"
        def="0.08"
      >
        {t('eyeDocs.parameters.scleraFilmRoughness')}
      </Dial>
      <Note label={t('eyeDocs.parameters.noteSurfaceLabel')}>
        {t('eyeDocs.parameters.noteSurface')}
      </Note>

      <H2 id="highlight">{t('squarebitEye.sectionHighlight')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.parameters.highlightIntro"
          components={{ i: <em /> }}
        />
      </Body>
      <Dial nameKey="squarebitEye.specIntensityLabel" range="0–8" def="0">
        {t('eyeDocs.parameters.specIntensity')}
      </Dial>
      <Dial nameKey="squarebitEye.specXLabel" range="−1–1" def="0.2">
        {t('eyeDocs.parameters.specX')}
      </Dial>
      <Dial nameKey="squarebitEye.specYLabel" range="−1–1" def="0.15">
        {t('eyeDocs.parameters.specY')}
      </Dial>
      <Dial nameKey="squarebitEye.specSizeLabel" range="0.01–0.6" def="0.08">
        {t('eyeDocs.parameters.specSize')}
      </Dial>
      <Dial
        nameKey="squarebitEye.specSidesLabel"
        range={`${t('squarebitEye.specShapeCircle')} / ${t('squarebitEye.specShapeTriangle')} / ${t('squarebitEye.specShapeSquare')}`}
        def={t('squarebitEye.specShapeCircle')}
      >
        {t('eyeDocs.parameters.specSides')}
      </Dial>
      <Dial nameKey="squarebitEye.specRoundnessLabel" range="0–1" def="0">
        {t('eyeDocs.parameters.specRoundness')}
      </Dial>
      <Dial nameKey="squarebitEye.specWideLabel" range="0.1–4" def="1">
        {t('eyeDocs.parameters.specWide')}
      </Dial>
      <Dial nameKey="squarebitEye.specTallLabel" range="0.1–4" def="1">
        {t('eyeDocs.parameters.specTall')}
      </Dial>
      <Dial nameKey="squarebitEye.specRotateLabel" range="−π–π" def="0">
        {t('eyeDocs.parameters.specRotate')}
      </Dial>
      <Dial nameKey="squarebitEye.specCoreLabel" range="0–0.999" def="0.35">
        {t('eyeDocs.parameters.specCore')}
      </Dial>
      <Dial nameKey="squarebitEye.specFalloffLabel" range="0.1–6" def="1">
        {t('eyeDocs.parameters.specFalloff')}
      </Dial>

      <H2 id="light">{t('squarebitEye.sectionLight')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.parameters.lightIntro"
          components={{
            b: <strong />,
            dir: <strong>lightDirWorld</strong>,
            col: <strong>lightColorWorld</strong>
          }}
        />
      </Body>
      <VarCard
        name={`${t('squarebitEye.lightAzLabel')} / ${t('squarebitEye.lightElLabel')}`}
        type="−180–180 / −60–80°"
        example="27 / 31"
      >
        {t('eyeDocs.parameters.lightAzEl')}
      </VarCard>
      <Dial
        nameKey="eyeDocs.parameters.lightColorName"
        range={colorType}
        def="#FFFFFF"
      >
        {t('eyeDocs.parameters.lightColor')}
      </Dial>
      <Dial nameKey="squarebitEye.lightStrengthLabel" range="0–5" def="2.2">
        {t('eyeDocs.parameters.lightStrength')}
      </Dial>
      <Dial nameKey="squarebitEye.scleraGlowLabel" range="0–1" def="0">
        {t('eyeDocs.parameters.scleraGlow')}
      </Dial>
      <Dial nameKey="squarebitEye.scleraScatterLabel" range="0–1" def="0.5">
        {t('eyeDocs.parameters.scleraScatter')}
      </Dial>
      <Dial nameKey="squarebitEye.socketAOLabel" range="0–1" def="0.35">
        {t('eyeDocs.parameters.socketOcclusion')}
      </Dial>
      <Dial nameKey="squarebitEye.irisFlattenLabel" range="0–1" def="0">
        {t('eyeDocs.parameters.irisFlatten')}
      </Dial>

      <DocFooter current="/squarebit-eye/docs/parameters" />
    </article>
  );
};

export default ParametersDoc;
